import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, CheckCircle2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const Stars = ({ label, value, onChange, testId }) => (
  <div className="mb-5" data-testid={`fb-${testId}`}>
    <div className="mb-1.5 text-sm font-semibold text-[#0E2A47]">{label}</div>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`fb-${testId}-${n}`}
          className="transition-transform hover:scale-110" aria-label={`${n} star`}>
          <Star size={30} className={n <= value ? 'fill-[#8FC93A] text-[#8FC93A]' : 'text-slate-300'} />
        </button>
      ))}
    </div>
  </div>
);

export default function WorkOrderFeedback() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ timeliness: 0, quality: 0, professionalism: 0, comments: '' });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/wo-feedback/${token}`)
      .then((r) => { setCtx(r.data); if (r.data.already_submitted) setDone(true); })
      .catch((e) => setErr(e.response?.data?.detail || 'This feedback link is not valid.'));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/wo-feedback/${token}`, {
        timeliness: form.timeliness, quality: form.quality, professionalism: form.professionalism,
        comments: form.comments || null,
      });
      setDone(true);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not submit feedback.'); }
    finally { setBusy(false); }
  };

  const ready = form.timeliness && form.quality && form.professionalism;

  return (
    <div className="min-h-screen bg-[#0E2A47] px-4 py-10 flex justify-center" data-testid="wo-feedback-page">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center text-white">
          <div className="text-2xl font-extrabold tracking-tight">Total Facility Solutions, Inc.</div>
          <div className="text-sm text-[#A3D65C]">How did we do?</div>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-xl">
          {err ? (
            <div className="py-8 text-center text-sm text-red-600" data-testid="fb-error">{err}</div>
          ) : !ctx ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : done ? (
            <div className="py-8 text-center" data-testid="fb-thanks">
              <CheckCircle2 size={40} className="mx-auto mb-3 text-[#8FC93A]" />
              <div className="text-lg font-bold text-[#0E2A47]">Thank you for your feedback!</div>
              <div className="mt-1 text-sm text-slate-500">We appreciate your business.</div>
            </div>
          ) : (
            <>
              <div className="mb-4 border-b border-slate-100 pb-3">
                <div className="text-xs uppercase tracking-wide text-slate-400">Service Feedback</div>
                <div className="text-base font-bold text-[#0E2A47]">{ctx.job_title || 'Completed Service'}</div>
                <div className="text-xs text-slate-500">{[ctx.customer_name, ctx.service_location].filter(Boolean).join(' · ')}</div>
                <div className="text-xs text-slate-400">{ctx.work_order_number}{ctx.service_date ? ` · ${ctx.service_date}` : ''}</div>
              </div>
              <Stars label="Timeliness" value={form.timeliness} onChange={(v) => setForm((f) => ({ ...f, timeliness: v }))} testId="timeliness" />
              <Stars label="Quality of Work" value={form.quality} onChange={(v) => setForm((f) => ({ ...f, quality: v }))} testId="quality" />
              <Stars label="Professionalism" value={form.professionalism} onChange={(v) => setForm((f) => ({ ...f, professionalism: v }))} testId="professionalism" />
              <div className="mb-4">
                <div className="mb-1.5 text-sm font-semibold text-[#0E2A47]">Comments (optional)</div>
                <textarea rows={4} value={form.comments} onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-[#8FC93A] focus:outline-none"
                  placeholder="Tell us how we did…" data-testid="fb-comments" />
              </div>
              <button onClick={submit} disabled={!ready || busy} data-testid="fb-submit"
                className="w-full rounded-lg bg-[#8FC93A] py-3 font-semibold text-[#0E2A47] transition hover:bg-[#7fb82f] disabled:opacity-50">
                {busy ? 'Submitting…' : 'Submit Feedback'}
              </button>
            </>
          )}
        </div>
        <div className="mt-4 text-center text-[11px] text-slate-400">Your feedback goes directly to our Quality team.</div>
      </div>
    </div>
  );
}
